import * as React from 'react';
import { useCallback, type JSX } from 'react';
import { useFormContext, Controller, RegisterOptions, ControllerRenderProps, FieldValues } from 'react-hook-form';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { CheckboxGroup, FormLabelWrapper, TextInput, ICheckboxGroupProps } from '@restworld/ui-ds-base';

import { HelperTextRElement } from './form-inputs';
import { useWorkerApplicationCxt } from '../worker-application-wizard.context';

export const WizardMultiSelect: React.FunctionComponent<IAppProps> = ({
  options,
  placeholder,
  id,
  mainTitle,
  formLabel,
  disabled,
  rules,
  subTitle,
  errorMessage,
  isModal = true,
  isOrientation,
  variant,
  isSingleSelect,
  testId,
}) => {
  const { control, setValue } = useFormContext();
  const { setOverlayModalContent, errorsTextMap } = useWorkerApplicationCxt();
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, field: ControllerRenderProps<FieldValues, string>) => {
      e.stopPropagation();
      if (e.target.id === 'Others' || e.target.id === '') return;
      if (isSingleSelect) {
        field.onChange([e.target.id]);
        return;
      }
      let checkV: string[] = field.value ? [...field.value] : [];
      checkV =
        !checkV.includes(e.target.id) && e.target.checked
          ? [...checkV, e.target.id]
          : checkV.filter((v) => v !== e.target.id);
      field.onChange(checkV.length ? checkV : undefined);
    },
    [isSingleSelect]
  );
  const checkBoxGroupEl = useCallback(
    (field: ControllerRenderProps<FieldValues, string>) => (
      <CheckboxGroup
        key={id}
        options={options ? [...options] : []}
        mode="clickable"
        variant={variant ? variant : isModal ? 'box' : 'chip'}
        isWrapList={false}
        mainTitle={isModal ? mainTitle || formLabel : undefined}
        subTitle={subTitle}
        isOrientation={isOrientation}
        {...{
          ...field,
          onChange: (e) => onChange(e, { ...field }),
        }}
        setValue={setValue}
        testId={testId}
      />
    ),
    [formLabel, id, isModal, isOrientation, mainTitle, onChange, options, setValue, subTitle, testId, variant]
  );
  const handleClick = useCallback(() => {
    setOverlayModalContent({
      visible: true,
      content: (
        <Controller key={id} name={id} control={control} render={({ field }) => checkBoxGroupEl({ ...field })} />
      ),
    });
  }, [setOverlayModalContent, id, control, checkBoxGroupEl]);
  return (
    <FormLabelWrapper id={id} label={formLabel}>
      <Controller
        key={id}
        name={id}
        rules={rules}
        control={control}
        render={({ field }) => {
          const selectedKeys: string[] | null = field.value;
          const selected =
            selectedKeys && options
              ? options.reduce((prev, curr) => {
                  if (selectedKeys?.includes(curr.id.toString())) prev.push(curr.label as never);
                  return prev;
                }, [])
              : [];
          return isModal ? (
            <TextInput
              readOnly
              id={id}
              placeholder={placeholder}
              type="text"
              helperText={<HelperTextRElement helperText={errorMessage || errorsTextMap[id]} />}
              color={errorMessage || errorsTextMap[id] ? 'failure' : undefined}
              rightIcon={ChevronDownIcon}
              disabled={disabled}
              {...{
                ...field,
                value: selected && Array.isArray(selected) ? selected?.join(', ') : field.value,
              }}
              onClick={handleClick}
              testId={`${testId}-input`}
            />
          ) : (
            <>
              {checkBoxGroupEl({ ...field })}
              {errorMessage ? <HelperTextRElement helperText={errorMessage as string} /> : null}
            </>
          );
        }}
      />
    </FormLabelWrapper>
  );
};
export default WizardMultiSelect;
interface IAppProps extends ICheckboxGroupProps {
  formLabel: string | JSX.Element;
  id: string;
  placeholder?: string;
  disabled?: boolean;
  rules: RegisterOptions;
  errorMessage?: string;
  isModal?: boolean;
  isSingleSelect?: boolean;
  testId?: string;
}
